<script lang="ts">
export default {
  name: 'KkApplyModal',
}
</script>
<script setup lang="ts">
import { computed } from 'vue'
import { KkButton, KkModal, KkIcon, KkBox, KkTextField, KkValidationError } from '../../../../components/base'
import { useI18n } from 'vue-i18n'
import { useForm, useField } from 'vee-validate'

const { t } = useI18n()

interface Emits {
  (e: 'close'): void
  (e: 'apply', phoneNumber: string): void
}
const emit = defineEmits<Emits>()

interface Service {
  url: string
  iconUrl: string
  name: string
  academyUrl?: string
}
interface Company {
  name: string
  address: string
  siteUrl: string
}
interface Content {
  id: number
  title: string
  text: string
  notes?: readonly string[]
  dueDate?: string
  applicationUrl?: string
}
interface Benefit {
  title: string
  eyeCatchUrl: string
  service: Service
  company: Company
  contents: readonly Content[]
  planAndPriceDescription: string
}

type Props = {
  isVisible: boolean
  benefit: Benefit
  industry: string
  brand: string
  applicant: string
  email: string
  companyName: string
  content: Content
  isDocomoPlan: boolean
}
const props = defineProps<Props>()
const schema = {
  phoneNumber: props.content.applicationUrl || props.isDocomoPlan ? '' : 'required|numeric'
}
const { handleSubmit, meta } = useForm({
  validationSchema: schema,
  initialValues: {
    phoneNumber: '',
  },
})
const { value: phoneNumber, errorMessage: phoneNumberError, handleChange: phoneNumberHandleChange } = useField<string>('phoneNumber', undefined, { label: t('pages.CompaniesBenefits.applyModal.availablePhoneNumber') })
const submitLabel = computed(() => {
  let label = ''
  if(props.isDocomoPlan) {
    label = t('pages.CompaniesBenefits.applyModal.upgrade.submit')
  } else if (props.content.applicationUrl) {
    label = t('pages.CompaniesBenefits.applyModal.applicationLink')
  } else {
    label = t('labels.apply')
  }
  return label
})
const save = handleSubmit((value): void => {
  emit('apply', value.phoneNumber)
})
</script>
<template>
  <KkModal width="720px" :is-visible="props.isVisible" @change="emit('close')">
    <KkBox>
      <template #header>
        <p class="header">{{ t('pages.CompaniesBenefits.applyModal.title') }}</p>
      </template>
      <template #content>
        <div class="content">
          <div class="content__service">
            <img class="content__service__icon" :src="props.benefit.service.iconUrl" alt="">
            <p class="content__service__name">{{ props.benefit.service.name }}</p>
          </div>
          <div class="content__benefit">
            <div class="content__benefit__title">
              <KkIcon name="benefit" class="content__benefit__title__icon" />
              <p class="content__benefit__title__text">{{ props.content.title }}</p>
            </div>
            <div class="content__benefit__detail">
              {{ props.content.text }}
            </div>
            <div class="content__benefit__note">
              <ul>
                <li v-for="(note, index) in props.content.notes" :key="index">
                  {{ note }}
                </li>
              </ul>
            </div>
          </div>
          <hr v-if="!props.content.applicationUrl || isDocomoPlan" class="content__hr">
          <table v-if="!props.content.applicationUrl && !isDocomoPlan" class="content__company">
            <tr>
              <td>{{ t('pages.CompaniesBenefits.applyModal.company') }}</td>
              <td>{{ props.companyName }}</td>
            </tr>
            <tr>
              <td>{{ t('pages.CompaniesBenefits.applyModal.industry') }}</td>
              <td>{{ props.industry }}</td>
            </tr>
            <tr>
              <td>{{ t('pages.CompaniesBenefits.applyModal.brand') }}</td>
              <td>{{ props.brand }}</td>
            </tr>
            <tr>
              <td>{{ t('pages.CompaniesBenefits.applyModal.applicant') }}</td>
              <td>{{ props.applicant }}</td>
            </tr>
            <tr>
              <td>{{ t('pages.CompaniesBenefits.applyModal.email') }}</td>
              <td>{{ props.email }}</td>
            </tr>
          </table>
          <div v-if="isDocomoPlan" class="content__upgrade">
            <div class="content__upgrade__title">{{ t('pages.CompaniesBenefits.applyModal.upgrade.title') }}</div>
            <div class="content__upgrade__image">
              <KkIcon name="upgradeConversion" />
            </div>
          </div>
        </div>
      </template>
      <template #footer>
        <div class="footer">
          <div v-if="isDocomoPlan" class="footer__caution">{{ t('pages.CompaniesBenefits.applyModal.upgrade.caution') }}</div>
          <div v-if="!props.content.applicationUrl && !isDocomoPlan" class="footer__phoneNumber">
            <p class="footer__phoneNumber__text">{{ t('pages.CompaniesBenefits.applyModal.availablePhoneNumber') }}</p>
            <div>
              <KkTextField :model-value="phoneNumber" class="c-textField" :placeholder="$t('messages.noSettings')" @update:model-value="phoneNumberHandleChange" />
              <KkValidationError :text="phoneNumberError" />
            </div>
          </div>
          <div class="footer__buttonGroup">
            <div class="footer__item">
              <KkButton color="secondary" outlined :text="$t('labels.cancel')" @click="emit('close')" />
            </div>
            <div class="footer__item">
              <KkButton :disabled="!isDocomoPlan && !meta.valid" color="primary" outlined :text="submitLabel" @click="save" />
            </div>
          </div>
        </div>
      </template>
    </KkBox>
  </KkModal>
</template>
<style lang="scss" scoped>
@import "../../../../../../stylesheets/biz_spa/foundation/var";

$sp: 768px;
$iphone_se_width: 375px;
@mixin sp {
  @media (max-width: ($sp)) {
    @content;
  }
}
@mixin iphone_se {
  @media (max-width: ($iphone_se_width)) {
    @content;
  }
}
.header {
  font-size: 16px;
}
.content {
  height: 400px;
  @include iphone_se {
    height: 365px;
  }
  overflow-y: scroll;
  &__service {
    display: flex;
    align-items: center;
    &__icon {
      width: 40px;
      height: 40px;
      margin-right: 16px;
      border-radius: 4px;
    }
    &__name {
      font-weight: bold;
      font-size: 28px;
    }
  }
  &__benefit {
    margin-top: 24px;
    white-space: pre-wrap;
    &__title {
      display: flex;
      align-items: center;
      &__icon {
        position: relative;
        bottom: 2px;
        width: 20px;
        min-width: 20px;
        height: 18px;
        margin-right: 8px;
      }
      &__text {
        color: $color-accent-primary;
        font-weight: bold;
        font-size: 16px;
      }
    }
    &__detail {
      margin-top: 24px;
      white-space: pre-wrap;
    }
    &__note {
      opacity: .5;
      margin-top: 24px;
      font-size: 12px;
      ul {
        padding-left: 16px;
        li {
          list-style: initial;
          margin-bottom: 8px;
        }
      }
    }
  }
  &__upgrade {
    margin-top: 16px;
    &__title {
      margin-bottom: 16px;
    }
    &__image {
      img {
        width: 100%;
      }
    }
  }
  &__hr {
    height: 1px;
    margin-top: 24px;
    border: 0;
    background-color: #E4E7EB;
  }
  &__company {
    margin-top: 24px;
    font-weight: 12px;
    border-collapse: collapse;
    border-spacing: 24px;
    td:first-child {
      width: 120px;
      font-weight: bold;
      text-align: left;
    }
    td {
      padding-bottom: 24px;
      text-align: left;
    }
  }
}
.footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  &__phoneNumber {
    display: flex;
    align-items: center;
    &__text {
      margin-right: 8px;
      font-weight: bold;
      font-size: 12px;
    }
  }
  &__buttonGroup {
    display: flex;
    margin-left: auto;
  }
  &__item {
    margin-left: 12px;
  }
  &__caution {
    color: $color-accent-primary;
  }
  @include sp {
    flex-direction: column;
    &__phoneNumber {
      width: 100%;
      > div {
        flex-grow: 1;
      }
    }
    &__buttonGroup {
      margin-top: 16px;
    }
  }
  @include iphone_se {
    &__phoneNumber {
      &__text {
        width: 127px;
      }
    }
  }
}
</style>
